import {
  Box,
  Checkbox,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import { FC } from 'react';
import { AccountedBy, AccountingType } from 'v2-types/order';
import { formatMoney } from 'utils/helpers';
import { useFieldArray, UseFormReturn } from 'react-hook-form';
import NumberField from 'components/number-field';
import { createEntryForm } from '../../types';
import { canLinkEntry } from '../../utils/entry-eligibility';
import { TFunction } from 'i18next';

type TableChargesProps = {
  accounting: AccountingType[];
  methods: UseFormReturn<createEntryForm>;
  kindEntry: string[];
  filterText: string;
  t: TFunction;
  filterFields?: { description: boolean; name: boolean; role: boolean };
};

const getEntryDecimals = (entry: { amount: number; accountingEntry?: any } & Record<string, any>): number => {
  const anyEntry: any = entry as any;
  if (typeof anyEntry.decimals === 'number') return anyEntry.decimals;
  if (typeof anyEntry.accountingEntry?.decimals === 'number') return anyEntry.accountingEntry.decimals;
  const s = String(entry.amount);
  if (s.includes('e') || s.includes('E')) return 2;
  const dot = s.indexOf('.');
  if (dot === -1) return 2;
  const d = s.length - dot - 1;
  return Math.max(2, Math.min(d, 6));
};

const roundToDecimals = (value: number, decimals: number): number => {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
};

const getRemaining = (entry: { amount: number; accounted_amount: number } & Record<string, any>): number => (
  roundToDecimals(entry.amount - entry.accounted_amount, getEntryDecimals(entry as any))
);

const useStyles = makeStyles((theme: any) => ({
  containerTable: {
    maxHeight: '600px',
    overflow: 'scroll',
  },
  cellText: {
    fontSize: '0.8125rem', // 13px — compact table text
    '& .MuiFilledInput-root': {
      fontSize: '0.8125rem',
    },
    '& .MuiOutlinedInput-root': {
      fontSize: '0.8125rem',
    },
  },
  amountText: {
    fontSize: '0.8125rem', // 13px — matches cellText
    textAlign: 'right',
    color: '#000000',
    fontFamily: '"Roboto Mono", "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace',
    '& .MuiFilledInput-root': {
      fontSize: '0.8125rem',
      fontFamily: '"Roboto Mono", "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace',
    },
    '& .MuiOutlinedInput-root': {
      fontSize: '0.8125rem',
      fontFamily: '"Roboto Mono", "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace',
    },
    '& .MuiFilledInput-input': {
      textAlign: 'right',
    },
    '& .MuiOutlinedInput-input': {
      textAlign: 'right',
    },
  },
  headerCell: {
    backgroundColor: theme.palette.grey[100],
    fontWeight: 600,
  },
  // Phones: each row becomes a two-line block — checkbox + description on top, full-width Amount below.
  table: {
    [theme.breakpoints.down('sm')]: {
      '& thead': { display: 'none' },
      '& tbody tr': {
        display: 'grid',
        gridTemplateColumns: 'auto minmax(0, 1fr)',
        alignItems: 'center',
        columnGap: theme.spacing(1),
        padding: theme.spacing(1, 1.5),
        borderBottom: `1px solid ${theme.palette.divider}`,
      },
      '& tbody td': {
        display: 'block',
        minWidth: 0,
        padding: theme.spacing(0.5, 0),
        borderBottom: 'none',
        textAlign: 'left',
      },
      '& tbody td:nth-child(3), & tbody td:nth-child(4)': { display: 'none' },
      '& tbody td:nth-child(1)': { gridColumn: 1, gridRow: 1, padding: 0 },
      '& tbody td:nth-child(2)': { gridColumn: 2, gridRow: 1 },
      '& tbody td:nth-child(5)': {
        gridColumn: 2,
        gridRow: 2,
        '& .MuiFormControl-root': { width: '100%' },
      },
    },
  },
  hideOnMobile: {
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
}));

const TableCharges: FC<TableChargesProps> = ({ accounting, methods, kindEntry, filterText, t, filterFields = { description: true, name: true, role: true } }) => {
  const {
    control,
    formState: { errors },
    setError,
    clearErrors,
  } = methods;

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: 'targets',
  });

  const classes = useStyles();

  const involvedPartiesFlatted = accounting
    .map(g => g.involved_parties.map(f => ({ ...f, accountingEntry: g })))
    .flat();

  const hasLetterAndNumber = (involved: typeof involvedPartiesFlatted[0]) => (
    canLinkEntry(involved.accountingEntry, kindEntry)
  );

  const matchingEntries = involvedPartiesFlatted
    .filter(hasLetterAndNumber)
    .filter((involved) => involved.accountingEntry.letter !== 'J')
    // Free-text filter: checkbox-gated search across entry description, party name, and party role (case-insensitive, matches ANY selected field)
    .filter((involved) => {
      if (!filterText) return true;
      const q = filterText.toLowerCase();
      const desc = (involved.accountingEntry.description || '').toLowerCase();
      const name = (involved.name || '').toLowerCase();
      const role = (involved.kind || '').toLowerCase();
      const orderRoles = (involved.order_kinds || []).join(' ').toLowerCase();
      return (
        (filterFields.description && desc.includes(q)) ||
        (filterFields.name && name.includes(q)) ||
        (filterFields.role && (role.includes(q) || orderRoles.includes(q)))
      );
    })
    // Party-role filter per tab: Disbursements show Payee associations,
    // Receipts show Payer associations, NetFunded shows all Payer associations.
    .filter((involved) => {
      if (kindEntry.includes('netfunded')) {
        return involved.kind === 'Payer';
      }
      if (kindEntry.includes('disbursement')) {
        return involved.kind === 'Payee';
      }
      if (kindEntry.includes('receipt')) {
        return involved.kind === 'Payer';
      }
      return true;
    })
    .filter((involved) => getRemaining(involved) !== 0);

  // Keep already-checked rows visible even when the filter would hide them —
  // otherwise changing the filter while rows are selected silently loses the selection.
  const checkedKeys = new Set(
    fields
      .filter((f) => f.checked)
      .map((f) => `${f.target_entry_id}-${f.target_association_id}`),
  );

  const filteredEntries = [
    ...matchingEntries,
    ...involvedPartiesFlatted.filter((involved) => {
      if (!hasLetterAndNumber(involved)) return false;

      const key = `${involved.accountingEntry._id}-${involved._id}`;
      const alreadyVisible = matchingEntries.some(
        (v) => `${v.accountingEntry._id}-${v._id}` === key,
      );
      return !alreadyVisible && checkedKeys.has(key);
    }),
  ];

  const getField = (entry: typeof filteredEntries[0]) =>
    fields.find((f) => f.target_association_id === entry._id && f.target_entry_id === entry.accountingEntry._id);

  const getFieldIndex = (fieldId: string) =>
    fields.findIndex((f) => f.id === fieldId);

  const handleCheck = (checked: boolean, entry: typeof filteredEntries[0]) => {
    if (checked) {
      const amount = getRemaining(entry);
      append({
        checked: true,
        target_entry_id: entry.accountingEntry._id,
        target_association_id: entry._id,
        target_association_kind: entry.kind,
        amount,
      });
    } else {
      const field = getField(entry);
      if (field) {
        const idx = getFieldIndex(field.id);
        if (idx !== -1) {
          remove(idx);
        }
      }
    }
  };

  // Pad numeric input values to at least 2 decimals so the Amount field always
  // shows cents ($2,500 -> $2,500.00, $760.5 -> $760.50), while values with more
  // decimals keep their full precision ($760.5555 stays $760.5555).
  const padAmountToMinTwoDecimals = (n: number): string => {
    if (n === null || n === undefined || Number.isNaN(n)) return '';
    const s = String(n);
    const dotIndex = s.indexOf('.');
    if (dotIndex === -1) return `${s}.00`;
    const decimalCount = s.length - dotIndex - 1;
    return decimalCount < 2 ? n.toFixed(2) : s;
  };

  const handleAmountChange = (amount: number, entry: typeof filteredEntries[0]) => {
    const field = getField(entry);
    if (!field) return;

    const idx = getFieldIndex(field.id);
    if (idx === -1) return;

    const decimals = getEntryDecimals(entry as any);
    const maxAmount = roundToDecimals(entry.amount - entry.accounted_amount, decimals);

    update(idx, { ...fields[idx], amount });

    if (amount > maxAmount) {
      setError(`targets.${idx}.amount`, {
        type: 'manual',
        message: `${t('disbursments:error-max-amount')} ${formatMoney(maxAmount)}`,
      });
    } else if (errors.targets?.[idx]?.amount) {
      clearErrors(`targets.${idx}.amount`);
    }
  };

  if (filteredEntries.length === 0) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        style={{ padding: '24px 0', margin: '20px 0' }}
      >
        <Typography variant="body1" color="textSecondary">
          {t('disbursments:no-entries-found')}
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer
      component={Paper}
      style={{ maxHeight: 'min(450px, 50vh)', overflow: 'auto', margin: '20px 0' }}
    >
      <Table stickyHeader className={classes.table}>
        <TableHead>
          <TableRow>
            <TableCell className={classes.headerCell}>
              <></>
            </TableCell>
            <TableCell className={classes.headerCell}>
              <Typography className={classes.cellText}>{t('disbursments:description')}</Typography>
            </TableCell>
            <TableCell className={clsx(classes.headerCell, classes.hideOnMobile)}>
              <Typography className={classes.cellText}>{t('disbursments:total')}</Typography>
            </TableCell>
            <TableCell className={clsx(classes.headerCell, classes.hideOnMobile)}>
              <Typography className={classes.cellText}>{t('disbursments:already-accounted')}</Typography>
            </TableCell>
            <TableCell className={classes.headerCell}>
              <Typography className={classes.cellText}>{t('disbursments:amount')}</Typography>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody className={classes.containerTable}>
          {filteredEntries.map((entry) => {
            const field = getField(entry);
            const entryChecked = !!field;
            const entryAmount = field?.amount;
            const entryError = field ? errors.targets?.[getFieldIndex(field.id)]?.amount : undefined;

            return (
              <TableRow key={`${entry._id}-${entry.accountingEntry._id}`}>
                <TableCell>
                  <Checkbox
                    checked={entryChecked}
                    onChange={(e) => {
                      handleCheck(e.target.checked, entry);
                    }}
                  />
                </TableCell>
                <TableCell>
                  {(() => {
                    const { description } = entry.accountingEntry;
                    const toPrefix = ' to ';

                    // Strip the entire trailing payee list ("... to Payee1, Payee2, ...")
                    // from the description — every payee is already its own independent
                    // entry row with its own pill, so the description line shows only
                    // the base text (e.g. "C-1 Lender's Policy").
                    const toIndex = description.toLowerCase().lastIndexOf(toPrefix);
                    const displayDescription =
                      toIndex !== -1 ? description.slice(0, toIndex) : description;

                    if (entry.kind === 'Payer') {
                      const roles = entry.order_kinds?.length ? ` (${entry.order_kinds.join(', ')})` : '';
                      return (
                        <Box display="flex" flexDirection="column" alignItems="flex-start">
                          <Typography className={classes.cellText}>{displayDescription}</Typography>
                          <Chip
                            size="small"
                            variant="outlined"
                            label={`from ${entry.name}${roles}`}
                            style={{ marginTop: 4, fontSize: 11 }}
                          />
                        </Box>
                      );
                    }

                    const roles = entry.order_kinds?.length ? ` (${entry.order_kinds.join(', ')})` : '';
                    return (
                      <Box display="flex" flexDirection="column" alignItems="flex-start">
                        <Typography className={classes.cellText}>{displayDescription}</Typography>
                        <Chip
                          size="small"
                          variant="outlined"
                          label={`to ${entry.name}${roles}`}
                          style={{ marginTop: 4, fontSize: 11 }}
                        />
                      </Box>
                    );
                  })()}
                </TableCell>
                <TableCell align="right" className={classes.hideOnMobile}>
                  <Typography className={classes.amountText}>{formatMoney(entry.amount, getEntryDecimals(entry as any))}</Typography>
                </TableCell>
                <TableCell align="right" className={classes.hideOnMobile}>
                  <Typography className={classes.amountText}>{formatMoney(roundToDecimals(entry.accounted_amount, getEntryDecimals(entry as any)), getEntryDecimals(entry as any))}</Typography>
                </TableCell>
                <TableCell align="right">
                  <NumberField
                    className={classes.amountText}
                    disabled={!entryChecked}
                    decimalScale={null}
                    value={padAmountToMinTwoDecimals(
                      entryAmount !== undefined ? entryAmount : getRemaining(entry),
                    )}
                    thousandSeparator=","
                    prefix="$"
                    label={t('disbursments:amount')}
                    onChange={undefined}
                    onValueChange={(e) => {
                      const amount = e.floatValue || 0;
                      handleAmountChange(amount, entry);
                    }}
                    error={!!entryError}
                    helperText={
                      entryError?.message
                        ? t(`disbursments:${entryError.message}`)
                        : ''
                    }
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default TableCharges;
